/**
 * Loader implementation for EnginePreProcess
 * Handles loading and preprocessing of HTML templates with pre-calculated replacement mappings
 * Templates are kept immutable within the loader
 *
 * Architecture:
 * - Loader: Load, Parse, PreProcess (creates PreprocessedTemplate with Placeholders, SlottedTemplates, etc.)
 * - Engine: Only Merging using pre-parsed structures (NO parsing)
 *
 * JSON merging always happens in the engine at merge time, never in the loader.
 */

import { Logger } from '../../../../Arshu/dist/logger/logger.js';
import { JsonObject } from '../../app/json/jsonValue.js';
import { PreProcessTemplateEntry, PreProcessTemplateStore, PreprocessedTemplate } from '../model/index.js';
import { DynamicResults } from '../../model/dynamicResults.js';
import { CommonUtil } from '../../common/commonUtil.js';
import { apiMethods, apiStaticMethods } from './loaderPreProcess_Api.js';
import { inheritanceMethods } from './loaderPreProcess_Inheritance.js';
import { dynamicMethods } from './loaderPreProcess_Dynamic.js';
import { mappingMethods, mappingStaticMethods } from './loaderPreProcess_Mapping.js';
import { accessorMethods, accessorStaticMethods } from './loaderPreProcess_Accessors.js';
import { lifecycleMethods } from './loaderPreProcess_Lifecycle.js';
import { configPropertyDescriptors, iloaderMethods } from './loaderPreProcess_ILoader.js';
import type { ILoaderTestSupport } from '../../base/loader/iLoaderTestSupport.js';
import type { IConfig } from '../../config/iConfig.js';
import type { PreparedContent } from '../../config/preparedContent.js';
import { DynamicConfigMap } from '../../registry/dynamicConfigMap.js';
import type { AppSiteResult } from '../../registry/iface/appSiteResult.js';
import { ResourceStatistics, type InheritanceOperation, type NavigationDecision } from '../../model/index.js';
import type { AppSiteTemplateData } from '../../model/appSiteTemplateData.js';
import { ILoaderPreProcess, type GetTemplateResult, type GetTemplateWithViewResult } from './iLoaderPreProcess.js';
import { TransientStore } from '../../registry/transientStore.js';
import type { ITransientStore } from '../../registry/iface/iTransientStore.js';
import type { PhaseTiming } from '../../base/timing/phaseTiming.js';
import { TimingPhase } from '../../base/timing/timingPhase.js';
import type { RegistryStore } from '../../registry/registryStore.js';

type ReuseDataJsonResult = {
    jsonToUse: JsonObject | null;
    templateNameToUse: string;
};

/**
 * Loader implementation for EnginePreProcess
 * Handles loading and preprocessing of HTML templates with pre-calculated replacement mappings
 * Templates are kept immutable within the loader
 */
export class LoaderPreProcess extends ILoaderPreProcess implements ILoaderTestSupport {
    // #region Private Variables

    // Instance-level store - separates loaded data from per-merge mutable state
    _store: PreProcessTemplateStore = new PreProcessTemplateStore();


    _appSite: string = '';
    _config: IConfig | null = null;

    // Track Inheritance operations for flow data
    _inheritanceOperations: InheritanceOperation[] = [];

    // Per-merge mutable state for dynamic handler results
    // Engine creates and owns the DynamicResults; loader writes to it via setDynamicResults
    _dynamicResults: DynamicResults = new DynamicResults();
    _transientStore: ITransientStore = new TransientStore();

    // ActionMode flag - when true, DynamicAction handlers are executed
    _actionMode: boolean = false;

    // Skip patterns for DynamicView/DynamicData/DynamicAction handlers
    _skipDynamicView: string | null = null;
    _skipDynamicData: string | null = null;

    // Resource statistics are stored in _store

    // Pre-loaded stores (populated during Load, used during engine processing)
    _dynamicDataConfigStore: Record<string, DynamicConfigMap | null> = {};    // appSite -> DynamicConfigMap
    _dynamicClientDataConfigStore: Record<string, DynamicConfigMap | null> = {}; // appSite -> DynamicConfigMap
    _dynamicViewConfigStore: Record<string, DynamicConfigMap | null> = {};    // appSite -> DynamicConfigMap
    _dynamicActionConfigStore: Record<string, DynamicConfigMap | null> = {};  // appSite -> DynamicConfigMap
    _dynamicAppSiteConfigStore: Record<string, DynamicConfigMap | null> = {}; // appSite -> DynamicConfigMap
    _dynamicDataParamsStore: JsonObject = new JsonObject();    // "appSite:paramFile" -> JsonValue.object
    _dynamicClientDataParamsStore: JsonObject = new JsonObject(); // "appSite:paramFile" -> JsonValue.object
    _dynamicViewParamsStore: JsonObject = new JsonObject();    // "appSite:paramFile" -> JsonValue.object
    _dynamicActionParamsStore: JsonObject = new JsonObject();  // "appSite:paramFile" -> JsonValue.object
    _dynamicAppSiteParamsStore: JsonObject = new JsonObject(); // "appSite:paramFile" -> JsonValue.object

    // Per-merge request context (stashed at merge top from applyDynamicAppSiteHandlers). Mirrors the
    // C# LoaderPreProcess request context. Reserved for the AppSite/authorization phase (later phase).
    _requestContextJson: JsonObject | null = null;
    // Per-merge navigation trace (accumulated by applyDynamicAppSiteHandlers; drained by the engine into
    // MergeResult.navigationTrace via getAndClearNavigationTrace). Mirrors _inheritanceOperations ownership.
    _lastNavigationTrace: NavigationDecision[] = [];
    _globalDataStore: Record<string, JsonObject | null> = {};           // appSite -> JsonObject
    _globalViewDataStore: Record<string, JsonObject | null> = {};       // appSite -> JsonObject
    _globalViewDataComponentStore: Record<string, JsonObject | null> = {}; // "appSite:componentName" -> JsonObject
    _globalData: Map<string, JsonObject> | null = null;
    _globalViewData: Map<string, JsonObject> | null = null;
    _dynamicDataParams: JsonObject | null = null;
    _dynamicViewParams: JsonObject | null = null;
    _dynamicActionParams: JsonObject | null = null;

    // #endregion

    // #region Mixin Method Declarations
    // Methods attached via Object.assign from mixin files - declared with definite assignment

    // From loaderPreProcess_Accessors.ts (accessorMethods)
    declare load: (config: IConfig, appSite: string, timing: PhaseTiming) => boolean;
    declare getBaseComponentName: (placeholderOrSlotholderName: string, currentTemplateName: string) => { baseComponentName: string; isReuseComponent: boolean };
    declare getDynamicDataConfig: (appSite: string) => DynamicConfigMap | null;
    declare setDynamicResults: (dynamicResults: DynamicResults) => void;
    declare getDynamicActionConfig: (appSite: string) => DynamicConfigMap | null;
    declare getDynamicDataParams: (appSite: string, paramFile: string) => JsonObject | null;
    declare getDynamicClientDataConfig: (appSite: string) => DynamicConfigMap | null;
    declare getDynamicClientDataParams: (appSite: string, paramFile: string) => JsonObject | null;
    declare getDynamicViewConfig: (appSite: string) => DynamicConfigMap | null;
    declare getDynamicViewParams: (appSite: string, paramFile: string) => JsonObject | null;
    declare getDynamicActionParams: (appSite: string, paramFile: string) => JsonObject | null;
    declare getDynamicAppSiteConfig: (appSite: string) => DynamicConfigMap | null;
    declare getDynamicAppSiteParams: (appSite: string, paramFile: string) => JsonObject | null;
    declare ensureAppSiteLoaded: (appSite: string, timing: PhaseTiming) => boolean;
    declare getGlobalData: (appSite: string) => JsonObject | null;
    declare getGlobalViewData: (appSite: string) => JsonObject | null;
    declare getGlobalViewDataForComponent: (appSite: string, componentName: string) => JsonObject | null;
    declare setActionMode: (actionMode: boolean) => void;
    declare getAndClearInheritanceOperations: () => InheritanceOperation[];
    declare getAndClearNavigationTrace: () => NavigationDecision[];
    declare getAppFile: (appSite: string, appView: string) => { appFile: string; validatedAppView: string };
    declare _preloadDynamicAndGlobalData: (config: IConfig, prepared: PreparedContent, appSite: string) => void;
    declare getTemplatesApiResponse: (appSite: string, appView: string, contextJson: JsonObject, serverTimeMs: number, includeSearchAppSites?: boolean, maskHtml?: boolean, orderedTemplateNames?: string[] | null) => string;
    declare setTransientStore: (transientStore: ITransientStore) => void;
    declare getTransientStore: () => ITransientStore;
    declare _serializePerSiteStores: (sb: string[], appSite: string) => void;
    declare _serializeDynamicConfigToJson: (config: DynamicConfigMap) => string;
    declare _serializePerSiteDynamicConfig: (sb: string[], allSites: string[], key: string, getter: (site: string) => DynamicConfigMap | null) => void;
    declare _serializePerSiteParamsFromStore: (sb: string[], allSites: string[], key: string, paramsStore: JsonObject) => void;
    declare getComponentId: (appSite: string, componentName: string) => string | null;
    declare getSearchAppSitesForAppSite: (appSite: string) => string;
    declare getLoadedTemplates: (appSite: string) => Map<string, PreProcessTemplateEntry>;
    declare getResourceStatistics: (appSite: string) => ResourceStatistics;
    declare _computeResourceStatistics: (appSite: string) => ResourceStatistics;
    declare getLoaderStateJson: (appSite: string) => string;
    declare getLoaderPerSiteStoresJson: (appSite: string) => string;

    // From loaderPreProcess_Api.ts (apiMethods)
    declare _getAllTemplatesJson: (includeSearchAppSites?: boolean, orderedTemplateNames?: string[] | null) => Record<string, AppSiteTemplateData>;
    declare _buildTemplatesJsonForAppSite: (appSite: string, orderedTemplateNames?: string[] | null) => string;
    declare _maskOriginalText: (value: string) => string;

    // From loaderPreProcess_Inheritance.ts (inheritanceMethods)
    declare _hasDynamicDataHandlers: (appSite: string, componentName: string) => boolean;
    declare _hasDynamicActionHandlers: (appSite: string, componentName: string) => boolean;
    declare _hasGlobalData: (appSite: string, componentName: string, resolvedAppSite: string | null) => boolean;
    declare getTemplateDataWithInheritance: (appSite: string, appView: string, appFile: string, jsonObj: JsonObject, resolvedTemplateName: string, timing: PhaseTiming, flowStack?: string[] | null, resolvedAppSite?: string | null, configJson?: JsonObject | null, componentHtml?: string | null, clientMode?: boolean) => JsonObject | null;
    declare searchContextStackForKey: (appSite: string, key: string, flowStack: string[]) => { value: string | null; sourceTemplate: string | null };
    declare _resolveJsonKeyWithInheritance: (appSite: string, actualKey: string, defaultValue: string, currentTemplateKey: string, flowStack?: string[] | null) => { resolvedValue: string | null; sourceTemplate: string | null; wasInherited: boolean };
    declare searchContextStackForArrayKey: (appSite: string, key: string, flowStack: string[]) => { value: import('../../app/json/index.js').JsonArray | null; sourceTemplate: string | null };
    declare _resolveJsonArrayKeyWithInheritance: (appSite: string, actualKey: string, defaultArray: import('../../app/json/index.js').JsonArray, currentTemplateKey: string, flowStack?: string[] | null) => { resolvedArray: import('../../app/json/index.js').JsonArray; sourceTemplate: string | null; wasInherited: boolean };
    declare _buildParentContext: (appSite: string, flowStack: string[]) => Map<string, string> | null;
    declare _applyGlobalData: (appSite: string, componentName: string, componentJson: JsonObject, resolvedAppSite: string | null) => void;
    declare _getWithFallback: (targetAppSite: string, appSite: string, getter: (site: string) => JsonObject | null) => JsonObject | null;
    declare _getGlobalDataWithFallback: (targetAppSite: string, appSite: string) => JsonObject | null;
    declare _getGlobalViewDataForComponentWithFallback: (targetAppSite: string, appSite: string, componentName: string) => JsonObject | null;
    declare _mergeGlobalDataIntoComponent: (componentJson: JsonObject, globalData: JsonObject | null) => void;
    declare _getScalarString: (val: import('../../app/json/jsonValue.js').JsonValue) => string | null;
    declare _matchesByStringKeys: (componentItem: JsonObject, globalItem: JsonObject, primaryKey: string | null) => boolean;
    declare _findKeyIgnoreCase: (data: JsonObject, key: string) => string | null;

    // From loaderPreProcess_Dynamic.ts (dynamicMethods)
    declare applyViewToTemplate: (appSite: string, currentTemplateName: string, componentName: string, timing: PhaseTiming, appView: string | null, flowStack: string[] | null, configJson: JsonObject | null, result: import('./iLoaderPreProcess.js').GetTemplateResult, clientMode?: boolean) => GetTemplateWithViewResult;
    declare executeWithReadOnly: <T>(paramJson: JsonObject, componentJson: JsonObject, configJson: JsonObject | null, action: () => T) => T;
    declare shouldSkipDynamicViewHandlerInvocation: (componentHtml: string, componentName: string, resolvedComponentName: string) => boolean;
    declare resolveDynamicViewConfigSource: (appSite: string) => { dynamicConfig: import('../../registry/dynamicConfigMap.js').DynamicConfigMap | null; dynamicConfigAppSite: string };
    declare getDynamicViewKeysToProcess: (appSite: string, componentName: string, dynamicConfig: import('../../registry/dynamicConfigMap.js').DynamicConfigMap, dynamicConfigAppSite: string) => { keysToProcess: string[]; dynamicConfig: import('../../registry/dynamicConfigMap.js').DynamicConfigMap; dynamicConfigAppSite: string };
    declare shouldSkipViewHandler: (iViewType: string) => boolean;
    declare tryResolveDynamicViewProvider: (iViewType: string, dynamicConfigAppSite: string, paramFile: string) => { viewProvider: import('../../registry/iface/iView.js').IView | null; paramJson: JsonObject };
    declare executeDynamicViewHandler: (appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, componentHtml: string, paramJson: JsonObject, componentJson: JsonObject, configJson: JsonObject | null, viewProvider: NonNullable<ReturnType<typeof RegistryStore.getDynamicView>>, timing: PhaseTiming) => string;
    declare recordDynamicViewResult: (componentName: string, iViewType: string, resultHtml: string) => void;
    declare resolveDynamicDataConfigSource: (appSite: string) => { dynamicConfig: import('../../registry/dynamicConfigMap.js').DynamicConfigMap | null; dynamicConfigAppSite: string };
    declare resolveDynamicClientDataConfigSource: (appSite: string) => { dynamicConfig: import('../../registry/dynamicConfigMap.js').DynamicConfigMap | null; dynamicConfigAppSite: string };
    declare tryResolvePureClientHandlerParams: (appSite: string, componentName: string, clientHandlerName: string) => JsonObject | null;
    declare _hasDynamicClientDataHandlers: (appSite: string, componentName: string) => boolean;
    declare applyDynamicClientDataHandlers: (appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, componentJson: JsonObject, configJson: JsonObject | null, timing: PhaseTiming, flowStack?: string[] | null) => void;
    declare applyClientHandlers: (appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, componentHtml: string | null, resolvedJson: JsonObject, configJson: JsonObject | null, timing: PhaseTiming, flowStack?: string[] | null) => void;
    declare preserveDeliveredClientData: (componentName: string) => void;
    declare getDynamicDataMatchKey: (componentName: string, dynamicConfig: import('../../registry/dynamicConfigMap.js').DynamicConfigMap) => string | null;
    declare shouldSkipDataHandler: (iDataType: string) => boolean;
    declare tryResolveDynamicDataProvider: (iDataType: string, dynamicConfigAppSite: string, paramFile: string) => { dataProvider: import('../../registry/iface/iData.js').IData | null; paramJson: JsonObject };
    declare executeDynamicDataHandler: (appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, paramJson: JsonObject, componentJson: JsonObject, configJson: JsonObject | null, dataHandler: NonNullable<ReturnType<typeof RegistryStore.getDynamicData>>, getParentJsonValue: ((key: string) => string | null) | null, timing: PhaseTiming) => JsonObject;
    declare mergeDynamicDataResult: (componentJson: JsonObject, resultJson: JsonObject | null) => void;
    declare replaceJsonObjectContents: (targetJson: JsonObject, sourceJson: JsonObject) => void;
    declare recordDynamicDataResult: (componentName: string, iDataType: string, componentJson: JsonObject) => void;
    declare syncDynamicActionResultsAfterDataMutation: (componentName: string, afterJson: unknown) => void;
    declare resolveDynamicActionConfigSource: (appSite: string) => { dynamicConfig: import('../../registry/dynamicConfigMap.js').DynamicConfigMap | null; dynamicConfigAppSite: string };
    declare tryResolveDynamicActionProvider: (iActionType: string, dynamicConfigAppSite: string, paramFile: string) => { registered: boolean; paramJson: JsonObject };
    declare tryBuildParentJsonValueHandlerForAction: (appSite: string, flowStack: string[] | null, contextJson?: JsonObject | null) => ((key: string) => string | null) | null;
    declare executeDynamicAction: (appSite: string, appView: string, appFile: string, componentName: string, resolvedAppSite: string, resolvedComponentName: string, paramJson: JsonObject, componentJson: JsonObject, actionJson: JsonObject | null, actionTypeName: string, getParentJsonValue: ((key: string) => string | null) | null, timing: PhaseTiming) => void;
    declare executeDynamicActionHandler: (appSite: string, appView: string, appFile: string, componentName: string, resolvedAppSite: string, resolvedComponentName: string, paramJson: JsonObject, componentJson: JsonObject, actionJson: JsonObject | null, actionTypeName: string, getParentJsonValue: ((key: string) => string | null) | null, timing: PhaseTiming) => void;
    declare recordDynamicActionResult: (componentName: string, iActionType: string, componentJson: JsonObject) => void;
    declare buildAppSiteSearchOrder: (appSite: string) => string[];
    declare applyDynamicViewHandlers: (appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, componentHtml: string, componentJson: JsonObject | null, resolvedAppSite: string, resolvedComponentName: string, configJson: JsonObject | null, timing: PhaseTiming) => { html: string; newPlaceholders: string[] };
    declare _applyDynamicViewHandlers: (appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, componentHtml: string, componentJson: JsonObject | null, resolvedAppSite: string, resolvedComponentName: string, configJson: JsonObject | null, timing: PhaseTiming) => { html: string; newPlaceholders: string[] };
    declare applyDynamicDataHandlers: (appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, componentJson: JsonObject, timing: PhaseTiming, configJson?: JsonObject | null, flowStack?: string[] | null) => void;
    declare applyDynamicActionHandlers: (appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, componentJson: JsonObject, resolvedAppSite: string, resolvedComponentName: string, timing: PhaseTiming, configJson?: JsonObject | null, contextJson?: JsonObject | null, flowStack?: string[] | null) => void;
    declare applyDynamicAppSiteHandlers: (appSite: string, appView: string | null, contextJson: JsonObject, configJson: JsonObject | null, timing: PhaseTiming) => AppSiteResult;

    // From loaderPreProcess_Mapping.ts (mappingMethods)
    declare _createAllReplacementMappingsWithCache: (templates: Map<string, PreProcessTemplateEntry>, appSite: string) => Map<string, PreProcessTemplateEntry>;

    // Static methods (declared separately below via Object.assign)
    static _escapeJsonString: (value: string | null | undefined) => string;
    static _serializeSlottedTemplatesArray: (slottedTemplates: PreprocessedTemplate['slottedTemplates']) => string;
    static _serializeSlotsArray: (slots: PreprocessedTemplate['slottedTemplates'][number]['slots']) => string;
    static loadTemplatesFromSingleAppSite: (config: IConfig, prepared: PreparedContent, appSite: string) => Map<string, PreProcessTemplateEntry>;
    static BuildAppSitesToLoad: (appSite: string, searchAppSites: string) => string[];
    static _shouldSkipHandler: (handlerType: string, skipPatterns: string | null) => boolean;
    static _countJsonKeysAndArrays: (json: JsonObject | null, stats: ResourceStatistics) => void;
    static _countHandlersInConfig: (config: JsonObject | DynamicConfigMap) => number;
    static _createPlaceholderReplacementMappings: (template: PreprocessedTemplate, currentTemplateName: string, allTemplates: Map<string, PreprocessedTemplate>, appSite: string, config: IConfig) => void;
    static _createSlottedTemplateReplacementMappings: (template: PreprocessedTemplate, allTemplates: Map<string, PreprocessedTemplate>, appSite: string, config: IConfig) => void;
    static _processSlotContentForReplacementMapping: (slot: PreprocessedTemplate['slottedTemplates'][number]['slots'][number], allTemplates: Map<string, PreprocessedTemplate>, appSite: string, config: IConfig, depth?: number) => string;

    // #endregion

    // #region Constructor

    /**
     * Creates a new LoaderPreProcess instance and automatically loads and preprocesses templates
     * @param config - Configuration interface providing file path metadata and SearchAppSites (required)
     * @param appSite - Primary AppSite name to load
     * @param timing - Records the load phases (source, parse, prepare, dynamic)
     */
    constructor(config: IConfig, appSite: string, timing: PhaseTiming) {
        super();
        timing.beginMeasure(TimingPhase.Prepare);
        try {
            this._appSite = '';
            this._config = config;
            this._inheritanceOperations = [];
            this._dynamicResults = new DynamicResults();
            this._actionMode = false;
            this.load(config, appSite, timing);
        } finally {
            timing.endMeasure();
        }
    }

    // #endregion

    // #region ILoader
    // NOTE: load() is in loaderPreProcess_ILoader.ts and loaderPreProcess_Accessors.ts
    // #endregion

    // #region Configuration
    // NOTE: Config wrapper getters (defaultReplaceTemplateName, defaultSkipTemplateName,
    // defaultReplaceHtml, defaultSkipHtml, defaultSkipJson, maxArrayOperationItems, maxRecursionDepth)
    // are defined in loaderPreProcess_ILoader.ts and attached via Object.defineProperties
    declare readonly defaultReplaceTemplateName: string;
    declare readonly defaultSkipTemplateName: string;
    declare readonly defaultReplaceHtml: string;
    declare readonly defaultSkipHtml: string;
    declare readonly defaultSkipJson: string;
    declare readonly defaultAppViewPrefix: string;
    declare readonly maxArrayOperationItems: number;
    declare readonly maxRecursionDepth: number;
    declare readonly maxMergeIterations: number;

    get defaultAppFile(): string { return this._config!.defaultAppFile; }
    // skipDynamicView/skipDynamicData property accessors are
    // attached from loaderPreProcess_Accessors.ts (mirrors C# LoaderPreProcess_Accessors.cs
    // partial-class file). See Object.defineProperties wiring below.
    declare skipDynamicView: string | null;
    declare skipDynamicData: string | null;

    // #endregion

    // #region ILoaderPreProcess

    // #region Retrieval Related

    /**
     * Gets a template's preprocessed HTML and JSON with AppView and SearchAppSites fallback support.
     * Returns the preprocessed template and resolved JSON (with inheritance).
     * If template is not found, returns a PreprocessedTemplate with DefaultReplaceHtml instead of null.
     *
     * IMPORTANT CONTRACT:
     * - currentTemplateName MUST be a clean template name WITHOUT AppSite prefix
     * - Example: "MainContent" NOT "JsonRule5A_MainContent"
     */
    getTemplate(appSite: string, currentTemplateName: string, appFileOrPlaceholderOrSlotholderName: string, timing: PhaseTiming, appView: string | null = null, flowStack: string[] | null = null, configJson: JsonObject | null = null, clientMode: boolean = false): GetTemplateResult {
        return timing.measure(TimingPhase.Bind, () => {
            // === VALIDATION ===
            if (!currentTemplateName) {
                throw new Error("currentTemplateName must not be empty - it's required for component reuse pattern detection");
            }

            // Use GetAppFile to get appropriate default if empty
            if (!appFileOrPlaceholderOrSlotholderName) {
                const result = this._config!.getAppFile(appSite, appView || '');
                appFileOrPlaceholderOrSlotholderName = result.appFile;
            }

            Logger.debug(`GetTemplate: appSite='${appSite}', currentTemplate='${currentTemplateName}', name='${appFileOrPlaceholderOrSlotholderName}', appView='${appView}'`, 'LoaderPreProcess');

            // === 1. CACHE CHECK ===
            const cached = this._store.tryGetTemplates(appSite);
            if (!cached) {
                Logger.debug(`GetTemplate: AppSite '${appSite}' not found in cache, returning defaults`, 'LoaderPreProcess');
                return {
                    template: this._createDefaultTemplate(),
                    json: null,
                    resolvedTemplateName: this._config!.defaultReplaceTemplateName,
                    resolvedAppSite: appSite
                };
            }

            const appSiteTemplates = cached;

            // === 2. SKIP MARKER CHECK ===
            if (appFileOrPlaceholderOrSlotholderName.startsWith('!')) {
                Logger.debug(`GetTemplate: Skip marker detected in '${appFileOrPlaceholderOrSlotholderName}'`, 'LoaderPreProcess');
                const skipTemplate = new PreprocessedTemplate();
                skipTemplate.originalContent = this._config!.defaultSkipHtml;
                skipTemplate.actualTemplateName = this._config!.defaultSkipTemplateName;
                skipTemplate.placeholders = [];
                skipTemplate.slottedTemplates = [];
                skipTemplate.replacementMappings = [];
                return {
                    template: skipTemplate,
                    json: null,
                    resolvedTemplateName: this._config!.defaultSkipTemplateName,
                    resolvedAppSite: appSite
                };
            }

            // Initialize tracking variables
            let template: PreprocessedTemplate | null = null;
            let json: JsonObject | null = null;
            let templateKey: string | null = null;
            let resolvedAppSite = appSite;

            // === 2.5. APPVIEW PRIORITY CHECK ===
            // When appView differs from DefaultAppViewPrefix, try view-specific template FIRST
            if (appView && this._config!.defaultAppViewPrefix &&
                appView.toLowerCase() !== this._config!.defaultAppViewPrefix.toLowerCase() &&
                appFileOrPlaceholderOrSlotholderName.toLowerCase().includes(this._config!.defaultAppViewPrefix.toLowerCase())) {
                const appViewName = CommonUtil.replaceCaseInsensitive(appFileOrPlaceholderOrSlotholderName, this._config!.defaultAppViewPrefix, appView);
                const appViewKey = `${appSite.toLowerCase()}_${appViewName.toLowerCase()}`;
                const appViewTemplate = appSiteTemplates.get(appViewKey);
                if (appViewTemplate && appViewTemplate.template.originalContent) {
                    Logger.debug(`GetTemplate: Found AppView-specific template: '${appViewKey}'`, 'LoaderPreProcess');
                    template = appViewTemplate.template;
                    json = appViewTemplate.json;
                    templateKey = appViewName;
                }
            }

            // === 3. DIRECT LOOKUP (FAST PATH) ===
            const directKey = `${appSite.toLowerCase()}_${appFileOrPlaceholderOrSlotholderName.toLowerCase()}`;
            const directTemplate = appSiteTemplates.get(directKey);
            if (!template && directTemplate && directTemplate.template.originalContent) {
                Logger.debug(`GetTemplate: Direct lookup found template: '${directKey}'`, 'LoaderPreProcess');
                template = directTemplate.template;
                json = directTemplate.json;
                templateKey = appFileOrPlaceholderOrSlotholderName;
            }

            // === 4. COMPONENT REUSE PATTERN CHECK ===
            let baseTemplateName = appFileOrPlaceholderOrSlotholderName;
            let reuseJsonName: string | null = null;
            if (!template) {
                const parseResult = CommonUtil.parseComponentReusePattern(appFileOrPlaceholderOrSlotholderName, currentTemplateName);
                baseTemplateName = parseResult.baseTemplateName;
                reuseJsonName = parseResult.reuseJsonName;

                if (parseResult.isReusePattern) {
                    Logger.debug(`GetTemplate: Component reuse detected - Original: '${appFileOrPlaceholderOrSlotholderName}', Base: '${baseTemplateName}'`, 'LoaderPreProcess');
                }
            }

            // === 5. APPVIEW FALLBACK ===
            if (!template && appView && this._config!.defaultAppViewPrefix &&
                baseTemplateName.toLowerCase().includes(this._config!.defaultAppViewPrefix.toLowerCase())) {
                const appKey = CommonUtil.replaceCaseInsensitive(baseTemplateName, this._config!.defaultAppViewPrefix, appView);
                const fallbackKey = `${appSite.toLowerCase()}_${appKey.toLowerCase()}`;
                const fallbackTemplate = appSiteTemplates.get(fallbackKey);
                if (fallbackTemplate) {
                    Logger.debug(`GetTemplate: Found via AppView fallback: '${fallbackKey}'`, 'LoaderPreProcess');
                    template = fallbackTemplate.template;
                    const { jsonToUse, templateNameToUse } = this.resolveReuseDataJson(appSiteTemplates, appSite, reuseJsonName, fallbackTemplate.json, appKey);
                    json = jsonToUse;
                    templateKey = templateNameToUse;
                }
            }

            // === 6. PRIMARY APPSITE LOOKUP ===
            if (!template) {
                const primaryKey = `${appSite.toLowerCase()}_${baseTemplateName.toLowerCase()}`;
                const primaryTemplate = appSiteTemplates.get(primaryKey);
                if (primaryTemplate) {
                    Logger.debug(`GetTemplate: Found primary template: '${primaryKey}'`, 'LoaderPreProcess');
                    template = primaryTemplate.template;
                    const { jsonToUse, templateNameToUse } = this.resolveReuseDataJson(appSiteTemplates, appSite, reuseJsonName, primaryTemplate.json, baseTemplateName);
                    json = jsonToUse;
                    templateKey = templateNameToUse;
                }
            }

            // === 7. SEARCHAPPSITES FALLBACK ===
            if (!template) {
                const searchResult = this.trySearchAppSitesLookup(appSite, appSiteTemplates, appView, baseTemplateName, reuseJsonName);
                if (searchResult) {
                    template = searchResult.template;
                    json = searchResult.json;
                    templateKey = searchResult.templateKey;
                    resolvedAppSite = searchResult.resolvedAppSite;
                }
            }

            // === 8. NOT FOUND - RETURN DEFAULT ===
            if (!template) {
                Logger.debug(`GetTemplate: Template not found: '${appFileOrPlaceholderOrSlotholderName}', returning defaults`, 'LoaderPreProcess');
                return {
                    template: this._createDefaultTemplate(),
                    json: null,
                    resolvedTemplateName: this._config!.defaultReplaceTemplateName,
                    resolvedAppSite: appSite
                };
            }

            // === 9. POST-PROCESSING ===
            // Resolve JSON inheritance if JSON exists
            let resolvedJson: JsonObject | null = null;
            if (json && templateKey) {
                resolvedJson = this.getTemplateDataWithInheritance(appSite, appView || '', appFileOrPlaceholderOrSlotholderName, json, templateKey, timing, flowStack, resolvedAppSite, configJson, template.originalContent, clientMode);
            }

            // NOTE: DynamicView handlers are applied by Engine via applyDynamicViewHandlers call (Area 7)

            return { template, json: resolvedJson as JsonObject | null, resolvedTemplateName: templateKey!, resolvedAppSite };
        });
    }

    getTemplateWithView(appSite: string, currentTemplateName: string, componentName: string, timing: PhaseTiming, appView: string | null = null, flowStack: string[] | null = null, configJson: JsonObject | null = null, clientMode: boolean = false): GetTemplateWithViewResult {
        return timing.measure(TimingPhase.Bind, () => {
            // clientMode threads into getTemplate → the inheritance seam, where applyClientHandlers consumes the
            // delivered island and enriches resolvedJson (the client mirror of the server data handler).
            return this.applyViewToTemplate(appSite, currentTemplateName, componentName, timing, appView, flowStack, configJson, this.getTemplate(appSite, currentTemplateName, componentName, timing, appView, flowStack, configJson, clientMode), clientMode);
        });
    }

    // #endregion

    // #endregion

    // #region Template Lookup Helpers (Private)

    /**
     * Helper method to create a default PreprocessedTemplate when template not found
     */
    _createDefaultTemplate(): PreprocessedTemplate {
        const template = new PreprocessedTemplate();
        template.originalContent = this._config!.defaultReplaceHtml;
        template.actualTemplateName = this._config!.defaultReplaceTemplateName;
        template.placeholders = [];
        template.slottedTemplates = [];
        template.replacementMappings = [];
        return template;
    }

    /**
     * Searches SearchAppSites for a matching template; within each search site the AppView-specific
     * name is tried before the base name.
     */
    trySearchAppSitesLookup(appSite: string, appSiteTemplates: Map<string, PreProcessTemplateEntry>, appView: string | null, baseTemplateName: string, reuseJsonName: string | null): { template: PreprocessedTemplate; json: JsonObject | null; templateKey: string; resolvedAppSite: string } | null {
        const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
        if (!searchAppSites) {
            return null;
        }

        const searchAppSitesArray = searchAppSites.split(',');
        for (const searchAppSite of searchAppSitesArray) {
            const trimmedSite = searchAppSite.trim();
            if (!trimmedSite) continue;

            const searchCached = this._store.tryGetTemplates(trimmedSite);
            if (!searchCached) continue;

            if (appView && this._config!.defaultAppViewPrefix &&
                baseTemplateName.toLowerCase().includes(this._config!.defaultAppViewPrefix.toLowerCase())) {
                const appKey = CommonUtil.replaceCaseInsensitive(baseTemplateName, this._config!.defaultAppViewPrefix, appView);
                const searchAppViewKey = `${trimmedSite.toLowerCase()}_${appKey.toLowerCase()}`;
                const searchAppViewTemplate = searchCached.get(searchAppViewKey);
                if (searchAppViewTemplate) {
                    Logger.debug(`GetTemplate: Found via AppView fallback in SearchAppSite: '${searchAppViewKey}'`, 'LoaderPreProcess');
                    const { jsonToUse, templateNameToUse } = this.resolveReuseDataJson(appSiteTemplates, appSite, reuseJsonName, searchAppViewTemplate.json, appKey);
                    return { template: searchAppViewTemplate.template, json: jsonToUse, templateKey: templateNameToUse, resolvedAppSite: trimmedSite };
                }
            }

            const searchKey = `${trimmedSite.toLowerCase()}_${baseTemplateName.toLowerCase()}`;
            const searchTemplate = searchCached.get(searchKey);
            if (searchTemplate) {
                Logger.debug(`GetTemplate: Template '${baseTemplateName}' not found in '${appSite}', using fallback from '${trimmedSite}'`, 'LoaderPreProcess');
                const { jsonToUse, templateNameToUse } = this.resolveReuseDataJson(appSiteTemplates, appSite, reuseJsonName, searchTemplate.json, baseTemplateName);
                return { template: searchTemplate.template, json: jsonToUse, templateKey: templateNameToUse, resolvedAppSite: trimmedSite };
            }
        }

        return null;
    }

    /**
     * Helper method to get reuse JSON or fall back to default
     * Returns: { jsonToUse, templateNameToUse }
     */
    resolveReuseDataJson(templates: Map<string, PreProcessTemplateEntry>, appSite: string, reuseJsonName: string | null, defaultJson: JsonObject | null, defaultTemplateName: string): ReuseDataJsonResult {
        if (!reuseJsonName) {
            return { jsonToUse: defaultJson, templateNameToUse: defaultTemplateName };
        }

        const reuseJsonKey = `${appSite.toLowerCase()}_${reuseJsonName.toLowerCase()}`;
        const reuseTemplate = templates.get(reuseJsonKey);
        if (reuseTemplate) {
            Logger.debug(`GetTemplate: Using reuse JSON from: '${reuseJsonKey}'`, 'LoaderPreProcess');
            return { jsonToUse: reuseTemplate.json, templateNameToUse: reuseJsonName };
        } else {
            // When reuse JSON is specified but not found, return null (not defaultJson)
            // This ensures missing JSON keys are replaced with DefaultReplaceHtml (*****)
            Logger.debug(`GetTemplate: Reuse JSON not found: '${reuseJsonKey}', returning null (no fallback)`, 'LoaderPreProcess');
            return { jsonToUse: null, templateNameToUse: reuseJsonName };
        }
    }

    // #endregion

    // #region ILoader Interface Implementation
    // NOTE: load(), getBaseComponentName(), getDynamicDataConfig(),
    // getAndClearInheritanceOperations() and other ILoader methods
    // are defined in loaderPreProcess_ILoader.ts and attached via Object.assign to prototype
    // #endregion

    // #region Statistics
    // NOTE: getResourceStatistics, _computeResourceStatistics, _countJsonKeysAndArrays, _countHandlersInConfig are in loaderPreProcess_Accessors.ts
    // #endregion
}

// Attach methods from split files (matching C# partial class pattern)
// Attach instance methods to prototype
// accessorMethods uses Object.defineProperties via Object.getOwnPropertyDescriptors so
// property accessor pairs (get/set skipDynamicView etc.) are preserved on the prototype
// instead of being collapsed to their initial returned value (Object.assign quirk).
Object.defineProperties(LoaderPreProcess.prototype, Object.getOwnPropertyDescriptors(accessorMethods));
Object.assign(LoaderPreProcess.prototype, apiMethods);
Object.assign(LoaderPreProcess.prototype, inheritanceMethods);
Object.assign(LoaderPreProcess.prototype, dynamicMethods);
Object.assign(LoaderPreProcess.prototype, mappingMethods);
Object.assign(LoaderPreProcess.prototype, lifecycleMethods);
Object.assign(LoaderPreProcess.prototype, iloaderMethods);

// Attach config property descriptors (getters) from ILoader file
Object.defineProperties(LoaderPreProcess.prototype, configPropertyDescriptors);

// Attach static methods to class
Object.assign(LoaderPreProcess, accessorStaticMethods);
Object.assign(LoaderPreProcess, apiStaticMethods);
Object.assign(LoaderPreProcess, mappingStaticMethods);
